import React, { useState, useRef, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { nanoid } from "nanoid";

import Auth from "shared/@auth";

import Modal from "../../Shared/Modal";
import ModalCard from "../../Shared/ModalCard";
import ModalActionBar from "shared/ModalActionBar";

import WebcamCapture from "./WebcamCapture";
import ImageEditor from "./ImageEditor";
import Input from "shared/@forms/Input";
import Button from "shared/Button";

import { ReactComponent as EditButtonImg } from "../../assets/Edit_Category_Edit.svg";
import { ReactComponent as CancelButtonImg } from "../../assets/Edit_Category_Cancel.svg";
import { ReactComponent as DoneButtonImg } from "../../assets/Edit_Category_Done.svg";
import { ReactComponent as SaveButtonImg } from "../../assets/Edit_Category_Save.svg";
import { ReactComponent as SwapButtonImg } from "../../assets/SwapIBOA_B.svg";

import styles from "./DiamondCardModal.module.css";

/**
 * Interactive component. Displays information about a diamond.
 * Has an option to edit.
 */

export function DiamondCardModal({
  isOpen,
  onClose,
  onSave,
  onSwap,
  diamondInfo,
  activeTab,
  isHideInfo,
}) {
  const { hasPermission } = Auth.useContainer();

  const readOnly = !hasPermission("management-team.admins");

  const imageRef = useRef();
  const chosenImageRef = useRef();
  const takenImageRef = useRef();

  const [currentDiamond, setCurrentDiamond] = useState(diamondInfo);

  const [isEdit, setIsEdit] = useState(false);
  const [isReplace, setIsReplace] = useState(false);
  const [isPhotoEdit, setIsPhotoEdit] = useState(false);
  const [webcamOpen, setWebcamOpen] = useState(false);
  const [iboATabActive, setIboATabActive] = useState(activeTab !== 2); //1 is A, 2 is B

  useEffect(() => {
    const checkClick = (e) => {
      if (
        isReplace &&
        imageRef.current &&
        !imageRef.current.contains(e.target)
      ) {
        setIsReplace(false);
      }
    };

    document.addEventListener("mousedown", checkClick);

    return () => {
      document.removeEventListener("mousedown", checkClick);
    };
  }, [isReplace]);

  const handleSave = () => {
    onSave(currentDiamond);
    setIsEdit(false);
  };

  const resetEdit = () => {
    setIsPhotoEdit(false);
  };

  const returnImageFile = (_ref) => {
    const reader = new FileReader();
    reader.readAsDataURL(_ref.current.files[0]);
    let file;
    reader.onload = () => {
      file = reader.result;
      iboATabActive
        ? setCurrentDiamond({
            ...currentDiamond,
            picture: file,
            isPhotoChanged: true,
          })
        : setCurrentDiamond({
            ...currentDiamond,
            isPhotoBChanged: true,
            iboB: {
              ...currentDiamond.iboB,
              photoUrl: file,
            },
          });
    };
  };

  const getCommitteesByProfile = (iboATabActive) => {
    return currentDiamond
      ? (iboATabActive
          ? currentDiamond.committees || []
          : currentDiamond.committeesB || []
        )
          .filter((com) => !com.deleted)
          .map((com) => {
            return {
              id: nanoid(),
              committeeName: com.committeeName,
              yearStarted: com.yearStarted,
            };
          })
      : [];
  };

  return (
    <>
      {webcamOpen && (
        <WebcamCapture
          onPhoto={(e) => {
            var reader = new FileReader();
            reader.readAsDataURL(e);

            reader.onloadend = function () {
              var base64data = reader.result;
              setCurrentDiamond({
                ...currentDiamond,
                picture: base64data,
                isPhotoChanged: true,
              });
            };

            setWebcamOpen(false);
          }}
          onCollapse={() => setWebcamOpen(false)}
        />
      )}
      <Modal
        isOpen={isOpen}
        className={styles.modalContainer}
        style="Hola"
        children={
          <div className={classNames(styles.container)}>
            <div className={classNames(styles["button-bar"])}>
              <ModalActionBar
                children={
                  <>
                    <button
                      style={{
                        visibility:
                          hasPermission("management-team.owners") &&
                          isEdit &&
                          currentDiamond.canSwap
                            ? "visible"
                            : "hidden",
                      }}
                      type="button"
                      onClick={() => {
                        onSwap(currentDiamond);
                      }}
                    >
                      <SwapButtonImg />
                    </button>
                    <button
                      style={{
                        visibility: hasPermission("management-team.owners")
                          ? "visible"
                          : "hidden",
                      }}
                      type="button"
                      onClick={() => {
                        if (isEdit) {
                          setCurrentDiamond({
                            ...diamondInfo,
                          });
                          setIsReplace(false);
                          resetEdit();
                        }
                        setIsEdit(!isEdit);
                      }}
                    >
                      {isEdit ? <CancelButtonImg /> : <EditButtonImg />}
                    </button>

                    <button
                      type="button"
                      onClick={isEdit ? () => handleSave() : onClose}
                    >
                      {isEdit ? <SaveButtonImg /> : <DoneButtonImg />}
                    </button>
                  </>
                }
              />
            </div>
            <div className={isEdit ? styles.modalCardContainer : ""}>
              {diamondInfo.iboB ? (
                <div className={styles.tabBar}>
                  <div
                    className={classNames(
                      styles.tab,
                      iboATabActive ? styles.tabIsActive : styles.tabIsInactive
                    )}
                    onClick={() => setIboATabActive(true)}
                  >
                    IBO A
                  </div>
                  <div
                    className={classNames(
                      styles.tab,
                      iboATabActive ? styles.tabIsInactive : styles.tabIsActive
                    )}
                    onClick={() => setIboATabActive(false)}
                  >
                    IBO B
                  </div>
                </div>
              ) : null}
              <ModalCard
                className={styles.customCard}
                children={
                  <div className={classNames(styles.card)}>
                    <div className={classNames(styles["top-flex"])}>
                      <label hidden htmlFor="chosen-profile-image">
                        Choose Profile Image
                      </label>
                      <input
                        ref={chosenImageRef}
                        onChange={() => returnImageFile(chosenImageRef)}
                        hidden
                        name="chosen-profile-image"
                        accept="image/jpeg, image/png"
                        type="file"
                      />
                      <label hidden htmlFor="taken-profile-image">
                        Take Profile Image
                      </label>
                      <input
                        type="file"
                        hidden
                        onChange={() => returnImageFile(takenImageRef)}
                        name="taken-profile-image"
                        ref={takenImageRef}
                        accept="image/*"
                        capture="user"
                      />
                      <div className={classNames(styles["image-swap"])}>
                        {isPhotoEdit ? (
                          <div className={styles.imageEditor}>
                            <ImageEditor
                              image={
                                iboATabActive
                                  ? currentDiamond.picture
                                  : currentDiamond.iboB.photoUrl
                              }
                              style={{ height: "94.72px", width: "94.72px" }}
                              updateImage={(e) => {
                                iboATabActive
                                  ? setCurrentDiamond({
                                      ...currentDiamond,
                                      picture: e,
                                      isPhotoChanged: true,
                                    })
                                  : setCurrentDiamond({
                                      ...currentDiamond,
                                      isPhotoBChanged: true,
                                      iboB: {
                                        ...currentDiamond.iboB,
                                        photoUrl: e,
                                      },
                                    });
                                setIsPhotoEdit(false);
                              }}
                              closeEditor={() => {
                                setIsPhotoEdit(false);
                              }}
                            />
                          </div>
                        ) : (
                          <div className={styles.imageContainer}>
                            <div className={styles["image-overflow"]}>
                              <img
                                src={
                                  iboATabActive
                                    ? currentDiamond.picture
                                    : currentDiamond.iboB.photoUrl
                                }
                                alt="Profile"
                                className={classNames(
                                  styles.img,
                                  styles.transition,
                                  {
                                    [styles.hidden]: isPhotoEdit,
                                  }
                                )}
                              />
                            </div>
                            {isEdit && (
                              <div className={styles.buttonRow}>
                                <Button
                                  type="button"
                                  className={styles["button-flex-button"]}
                                  children={<span>Replace</span>}
                                  onClick={() => setIsReplace(true)}
                                />

                                <div
                                  className={classNames(
                                    styles.transition,
                                    styles["button-flex"],
                                    {
                                      [styles.hidden]: isPhotoEdit,
                                    }
                                  )}
                                >
                                  <div
                                    ref={imageRef}
                                    className={classNames(
                                      styles["replace-image"],
                                      {
                                        [styles["hidden-replace"]]: !isReplace,
                                      }
                                    )}
                                  >
                                    <button
                                      onClick={() =>
                                        chosenImageRef.current.click()
                                      }
                                    >
                                      Photo Library<span></span>
                                    </button>
                                    <button onClick={() => setWebcamOpen(true)}>
                                      Take Photo or Video<span></span>
                                    </button>
                                    <button
                                      onClick={() =>
                                        chosenImageRef.current.click()
                                      }
                                    >
                                      Browse
                                    </button>
                                  </div>
                                </div>
                                <Button
                                  type="button"
                                  className={styles["button-flex-button"]}
                                  children={
                                    <span
                                      style={{
                                        color: "#243746",
                                        fontWeight: 300,
                                      }}
                                    >
                                      Edit
                                    </span>
                                  }
                                  onClick={() => setIsPhotoEdit(true)}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className={styles["flex-banner"]}>
                        <address className={classNames(styles.address)}>
                          <div>
                            <span className={classNames(styles.name)}>
                              {iboATabActive
                                ? currentDiamond.name
                                : `${currentDiamond.iboB.firstName} ${currentDiamond.iboB.lastName}`}
                            </span>
                            <div className={classNames(styles["name-spacing"])}>
                              {iboATabActive
                                ? currentDiamond.phoneNumber
                                : currentDiamond.iboB.phone}
                              <br />
                              {iboATabActive
                                ? currentDiamond.email
                                : currentDiamond.iboB.email}
                            </div>
                          </div>
                          <div className={styles.addressContainer}>
                            {iboATabActive && (
                              <div>
                                {currentDiamond.address &&
                                currentDiamond.address !== "N/A" &&
                                currentDiamond.address.address1 ? (
                                  <>
                                    <pre
                                      className={classNames(
                                        styles["address-pre"]
                                      )}
                                    >
                                      {currentDiamond.address.address1}
                                    </pre>
                                    <pre
                                      className={classNames(
                                        styles["address-pre"]
                                      )}
                                    >
                                      {currentDiamond.address.address2}
                                    </pre>
                                    <pre
                                      className={classNames(
                                        styles["address-pre"]
                                      )}
                                    >
                                      {`${currentDiamond.address.city}, ${currentDiamond.address.state} ${currentDiamond.address.postalCode}`}
                                    </pre>
                                  </>
                                ) : (
                                  "N/A"
                                )}
                              </div>
                            )}
                            {!iboATabActive && (
                              <div>
                                {currentDiamond.iboB?.address &&
                                currentDiamond.iboB?.address !== "N/A" &&
                                currentDiamond.iboB?.address.address1 ? (
                                  <>
                                    <pre
                                      className={classNames(
                                        styles["address-pre"]
                                      )}
                                    >
                                      {currentDiamond.iboB.address.address1}
                                    </pre>
                                    <pre
                                      className={classNames(
                                        styles["address-pre"]
                                      )}
                                    >
                                      {currentDiamond.iboB.address.address2}
                                    </pre>
                                    <pre
                                      className={classNames(
                                        styles["address-pre"]
                                      )}
                                    >
                                      {`${currentDiamond.iboB.address.city}, ${currentDiamond.iboB.address.state} ${currentDiamond.iboB.address.postalCode}`}
                                    </pre>
                                  </>
                                ) : (
                                  "N/A"
                                )}
                              </div>
                            )}
                          </div>
                        </address>
                      </div>
                    </div>

                    <div className={styles["flex-container"]}>
                      <div className={classNames(styles.grid)}>
                        {[false, true].map((bool) => (
                          <div
                            className={classNames(
                              styles.body,
                              styles["card-container"],
                              {
                                [styles.hidden]: bool ? !isEdit : isEdit,
                              }
                            )}
                          >
                            <div
                              className={classNames(styles.flex, {
                                [styles.disabled]: !bool,
                              })}
                            >
                              <div className={classNames(styles["flex-row"])}>
                                <div className={classNames(styles.input)}>
                                  <label htmlFor="spouse">Spouse</label>
                                  <Input
                                    name="spouse"
                                    value={
                                      iboATabActive
                                        ? currentDiamond.spouse
                                        : currentDiamond.iboB.spouse
                                    }
                                    disabled={!bool}
                                    onChange={(key) => {
                                      iboATabActive
                                        ? setCurrentDiamond({
                                            ...currentDiamond,
                                            spouse: key.target.value,
                                          })
                                        : setCurrentDiamond({
                                            ...currentDiamond,
                                            iboB: {
                                              ...currentDiamond.iboB,
                                              spouse: key.target.value,
                                            },
                                          });
                                    }}
                                  />
                                </div>
                                <div className={classNames(styles.input)}>
                                  <label htmlFor="ibo-number">IBO Number</label>
                                  <Input
                                    name="ibo-number"
                                    value={currentDiamond.iboNum}
                                    disabled={true}
                                    onChange={(key) =>
                                      setCurrentDiamond({
                                        ...currentDiamond,
                                        iboNum: key.target.value,
                                      })
                                    }
                                  />
                                </div>
                              </div>
                              <div className={classNames(styles["flex-row"])}>
                                <div className={classNames(styles.input)}>
                                  <label htmlFor="birthdate">Birthday</label>
                                  <Input
                                    name="birthdate"
                                    value={
                                      iboATabActive
                                        ? currentDiamond.birthdate
                                        : currentDiamond.iboB.birthdate
                                        ? currentDiamond.iboB.birthdate
                                        : ""
                                    }
                                    disabled={!bool}
                                    onChange={(key) => {
                                      iboATabActive
                                        ? setCurrentDiamond({
                                            ...currentDiamond,
                                            birthdate: key.target.value,
                                          })
                                        : setCurrentDiamond({
                                            ...currentDiamond,
                                            iboB: {
                                              ...currentDiamond.iboB,
                                              birthdate: key.target.value,
                                            },
                                          });
                                    }}
                                  />
                                </div>
                                <div className={classNames(styles.input)}>
                                  <label htmlFor="anniversary">
                                    Anniversary
                                  </label>
                                  <Input
                                    name="anniversary"
                                    value={
                                      iboATabActive
                                        ? currentDiamond.anniversary
                                        : currentDiamond.iboB.anniversary
                                        ? currentDiamond.iboB.anniversary
                                        : ""
                                    }
                                    disabled={!bool}
                                    onChange={(key) =>
                                      iboATabActive
                                        ? setCurrentDiamond({
                                            ...currentDiamond,
                                            anniversary: key.target.value,
                                          })
                                        : setCurrentDiamond({
                                            ...currentDiamond,
                                            iboB: {
                                              ...currentDiamond.iboB,
                                              anniversary: key.target.value,
                                            },
                                          })
                                    }
                                  />
                                </div>
                              </div>
                              <div
                                className={classNames(styles["bottom-divider"])}
                              />
                              <div className={classNames(styles.input)}>
                                <label htmlFor="pin-wearing">Pin Wearing</label>
                                <Input
                                  name="pin-wearing"
                                  value={currentDiamond.pinLevel}
                                  disabled={true}
                                  onChange={(key) =>
                                    setCurrentDiamond({
                                      ...currentDiamond,
                                      pinLevel: key.target.value,
                                    })
                                  }
                                />
                              </div>
                              <div className={classNames(styles.input)}>
                                <label htmlFor="upline-diamond">
                                  Upline Diamond
                                </label>
                                <Input
                                  name="upline-diamond"
                                  value={currentDiamond.uplineDiamond}
                                  disabled={!bool}
                                  onChange={(key) =>
                                    setCurrentDiamond({
                                      ...currentDiamond,
                                      uplineDiamond: key.target.value,
                                    })
                                  }
                                />
                              </div>
                              <div className={classNames(styles.input)}>
                                <label htmlFor="year-qualified">
                                  Year Qualified Diamond
                                </label>
                                <Input
                                  name="year-qualified"
                                  value={currentDiamond.yearDiamondQualified}
                                  disabled={true}
                                  onChange={(key) =>
                                    setCurrentDiamond({
                                      ...currentDiamond,
                                      yearDiamondQualified: key.target.value,
                                    })
                                  }
                                />
                              </div>
                              <div
                                className={classNames(
                                  styles["flex-row"],
                                  styles["grace-period"]
                                )}
                              >
                                <div className={classNames(styles.input)}>
                                  <label htmlFor="grace-period">
                                    Grace Period
                                  </label>
                                  <Input
                                    name="grace-period"
                                    value={currentDiamond.gracePeriod}
                                    disabled={!bool}
                                    onChange={(key) =>
                                      setCurrentDiamond({
                                        ...currentDiamond,
                                        gracePeriod: key.target.value,
                                      })
                                    }
                                  />
                                </div>
                                <div className={classNames(styles.input)}>
                                  <label htmlFor="grace-period-started">
                                    Grace Period Started
                                  </label>
                                  <Input
                                    name="grace-period-started"
                                    value={currentDiamond.gracePeriodStarted}
                                    disabled={!bool}
                                    onChange={(key) =>
                                      setCurrentDiamond({
                                        ...currentDiamond,
                                        gracePeriodStarted: key.target.value,
                                      })
                                    }
                                  />
                                </div>
                              </div>
                              <div
                                className={classNames(styles["bottom-divider"])}
                              />
                              <div className={classNames(styles["flex-row"])}>
                                <div className={classNames(styles.input)}>
                                  <label htmlFor="family-reunion">
                                    Family Reunion?
                                  </label>
                                  <Input
                                    name="family-reunion"
                                    value={currentDiamond.familyReunion}
                                    disabled={!bool}
                                    onChange={(key) =>
                                      setCurrentDiamond({
                                        ...currentDiamond,
                                        familyReunion: key.target.value,
                                      })
                                    }
                                  />
                                </div>
                                <div className={classNames(styles.input)}>
                                  <label htmlFor="first-family-reunion">
                                    First Family Reunion
                                  </label>
                                  <Input
                                    name="first-family-reunion"
                                    value={currentDiamond.firstFamilyReunion}
                                    disabled={!bool}
                                    onChange={(key) =>
                                      setCurrentDiamond({
                                        ...currentDiamond,
                                        firstFamilyReunion: key.target.value,
                                      })
                                    }
                                  />
                                </div>
                              </div>
                              <div
                                className={classNames(
                                  styles.input,
                                  styles["last-input"]
                                )}
                              >
                                <label htmlFor="reunion-attendance">
                                  Attendance at Last Years Family Reunion
                                </label>
                                <Input
                                  name="reunion-attendance"
                                  value={currentDiamond.familyReunionAttendance}
                                  disabled={!bool}
                                  onChange={(key) =>
                                    setCurrentDiamond({
                                      ...currentDiamond,
                                      familyReunionAttendance: key.target.value,
                                    })
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className={styles["vertical-line"]} />
                      <div className={classNames(styles.grid)}>
                        {[false, true].map((bool) => (
                          <div
                            className={classNames(
                              styles.body,
                              styles["card-container"],
                              {
                                [styles.hidden]: bool ? !isEdit : isEdit,
                              }
                            )}
                          >
                            <div
                              className={classNames(styles.flex, {
                                [styles.disabled]: !bool,
                              })}
                            >
                              <div className={classNames(styles["flex-row"])}>
                                <div className={classNames(styles.input)}>
                                  <label htmlFor="current-gar-points">
                                    Current Gar Points
                                  </label>
                                  {!isHideInfo ? (
                                    <Input
                                      name="current-gar-points"
                                      value={currentDiamond.garPoints}
                                      disabled={true}
                                      onChange={(key) =>
                                        setCurrentDiamond({
                                          ...currentDiamond,
                                          garPoints: key.target.value,
                                        })
                                      }
                                    />
                                  ) : (
                                    <div className={styles.hidePointIcon}>
                                      ***
                                    </div>
                                  )}
                                </div>
                                <div className={classNames(styles.input)}>
                                  <label htmlFor="integrated-gar-points">
                                    Integrated Gar Points
                                  </label>
                                  {!isHideInfo ? (
                                    <Input
                                      name="integrated-gar-points"
                                      value={currentDiamond.integratedGarPoints}
                                      disabled={true}
                                      onChange={(key) =>
                                        setCurrentDiamond({
                                          ...currentDiamond,
                                          integratedGarPoints: key.target.value,
                                        })
                                      }
                                    />
                                  ) : (
                                    <div className={styles.hidePointIcon}>
                                      ***
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className={classNames(styles["flex-row"])}>
                                <div className={classNames(styles.input)}>
                                  <label htmlFor="diamond-rank-gar">
                                    Diamond Rank (Gar)
                                  </label>
                                  {!isHideInfo ? (
                                    <Input
                                      name="diamond-rank-gar"
                                      value={currentDiamond.diamondRankGar}
                                      disabled={true}
                                      onChange={(key) =>
                                        setCurrentDiamond({
                                          ...currentDiamond,
                                          diamondRankGar: key.target.value,
                                        })
                                      }
                                    />
                                  ) : (
                                    <div className={styles.hidePointIcon}>
                                      ***
                                    </div>
                                  )}
                                </div>
                                <div className={classNames(styles.input)}>
                                  <label htmlFor="diamond-rank-wwg">
                                    Diamond Rank (WWG)
                                  </label>
                                  {!isHideInfo ? (
                                    <Input
                                      name="diamond-rank-wwg"
                                      value={currentDiamond.diamondRankWwg}
                                      disabled={true}
                                      onChange={(key) =>
                                        setCurrentDiamond({
                                          ...currentDiamond,
                                          diamondRankWwg: key.target.value,
                                        })
                                      }
                                    />
                                  ) : (
                                    <div className={styles.hidePointIcon}>
                                      ***
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div
                                className={classNames(styles["bottom-divider"])}
                              />
                              <div className={classNames(styles["flex-row"])}>
                                <div className={classNames(styles.input)}>
                                  <label htmlFor="voting-member">
                                    Voting Member
                                  </label>
                                  <Input
                                    name="voting-member"
                                    value={
                                      currentDiamond.votingMember ? "Yes" : "No"
                                    }
                                    disabled={true}
                                    onChange={(key) =>
                                      setCurrentDiamond({
                                        ...currentDiamond,
                                        votingMember: key.target.value,
                                      })
                                    }
                                  />
                                </div>
                                <div className={classNames(styles.input)}>
                                  <label htmlFor="year-qualified-voting">
                                    Year Voting Member{!isEdit && " Qualified"}
                                  </label>
                                  <Input
                                    name="year-qualified-voting"
                                    value={currentDiamond.votingMemberYear}
                                    disabled={!bool}
                                    onChange={(key) =>
                                      setCurrentDiamond({
                                        ...currentDiamond,
                                        votingMemberYear: key.target.value,
                                      })
                                    }
                                  />
                                </div>
                              </div>
                              <div className={classNames(styles["flex-row"])}>
                                <div className={classNames(styles.input)}>
                                  <label htmlFor="executive-committee">
                                    Executive Committee
                                  </label>
                                  <Input
                                    name="executive-committee"
                                    value={
                                      currentDiamond.executiveCommittee
                                        ? "Yes"
                                        : "No"
                                    }
                                    disabled={true}
                                    onChange={(key) =>
                                      setCurrentDiamond({
                                        ...currentDiamond,
                                        executiveCommittee: key.target.value,
                                      })
                                    }
                                  />
                                </div>
                                <div className={classNames(styles.input)}>
                                  <label
                                    hidden={
                                      !currentDiamond.executiveCommittee ||
                                      !currentDiamond.executiveCommittee ===
                                        true
                                    }
                                    htmlFor="year-ec-member"
                                  >
                                    Year EC Member
                                  </label>
                                  <Input
                                    hidden={
                                      !currentDiamond.executiveCommittee ||
                                      !currentDiamond.executiveCommittee ===
                                        true
                                    }
                                    name="year-ec-member"
                                    value={
                                      currentDiamond.yearExecutiveCommitteeQualified
                                    }
                                    disabled={!bool}
                                    onChange={(key) =>
                                      setCurrentDiamond({
                                        ...currentDiamond,
                                        yearExecutiveCommitteeQualified:
                                          key.target.value,
                                      })
                                    }
                                  />
                                </div>
                              </div>
                              <div
                                className={classNames(
                                  styles["committee-container"]
                                )}
                              >
                                <div
                                  className={classNames(
                                    styles["div-label"],
                                    styles["flex-row"]
                                  )}
                                >
                                  <p style={{ display: "flex", width: "100%" }}>
                                    Committees (Year Started)
                                  </p>
                                </div>
                                <div className={styles["committee-body"]}>
                                  {getCommitteesByProfile(iboATabActive).map(
                                    (committee) => (
                                      <div
                                        key={committee.id}
                                        className={styles["flex-row"]}
                                      >
                                        <div
                                          className={classNames(styles.input)}
                                          style={{
                                            display: "flex",
                                            width: "100%",
                                          }}
                                        >
                                          <label
                                            htmlFor={`committee-${committee.id}`}
                                            hidden
                                          >
                                            Committees (Year Started)
                                          </label>
                                          <p
                                            style={{
                                              marginTop: "1px",
                                              fontSize: "15px",
                                            }}
                                          >{`${committee.committeeName} (${committee.yearStarted})`}</p>
                                        </div>
                                      </div>
                                    )
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                }
              />
            </div>
          </div>
        }
      />
    </>
  );
}

const timeId = () => new Date().getTime();

DiamondCardModal.propTypes = {
  /**
   * Boolean that determines if the modal is displayed.
   */
  isOpen: PropTypes.bool,
  /**
   * Function that fires when the modal is closed.
   */
  onClose: PropTypes.func,
  /**
   * Function that fires when save is clicked.
   */
  onSave: PropTypes.func,
  /**
   * Object with diamond information.
   */
  diamondInfo: PropTypes.shape({
    picture: PropTypes.string,
    name: PropTypes.string,
    phoneNumber: PropTypes.string,
    email: PropTypes.string,
    address: PropTypes.object,
    accessLevel: PropTypes.oneOf(["EC", "MT1", "MT2", "GUEST", "None"]),
    spouse: PropTypes.string,
    iboNum: PropTypes.string,
    birthdate: PropTypes.string,
    anniversary: PropTypes.string,
    pinWearing: PropTypes.string,
    uplineDiamond: PropTypes.string,
    yearQualifyingRequirements: PropTypes.number,
    yearQualified: PropTypes.string,
    gracePeriod: PropTypes.bool,
    gracePeriodStarted: PropTypes.string,
    familyReunion: PropTypes.bool,
    firstFamilyReunion: PropTypes.string,
    familyReunionAttendance: PropTypes.string,
    garPoints: PropTypes.number,
    integratedGarPoints: PropTypes.number,
    diamondRankGar: PropTypes.number,
    diamondRankWwg: PropTypes.number,
    votingMember: PropTypes.bool,
    votingMemberYear: PropTypes.string,
    executiveCommittee: PropTypes.bool,
    committees: PropTypes.arrayOf(
      PropTypes.shape({
        committeeName: PropTypes.string,
        yearStarted: PropTypes.string,
      })
    ),
  }),
};
